import {
	type InformationGatheringDraft,
	type InformationGatheringPatientIdentity,
	type InformationGatheringRole,
} from '@feature/information-gathering/information-gathering.model';
import {
	type CompletedInformationGatheringState,
	createCompletedInformationGatheringState,
	type InformationGatheringEntry,
} from '@feature/information-gathering/information-gathering-navigation';
import { useInformationGathering } from '@feature/information-gathering/use-information-gathering';
import { useResolveController } from '@visiba/instantiation';
import { useCallback, useMemo } from 'react';
import { useStorage } from '../view_care_navigation/use_storage';
import { RedRobinScreen, ViewRedRobinController } from './view_red_robin.controller';

const EMPTY_PATIENT_IDENTITY: InformationGatheringPatientIdentity = { dateOfBirth: '', nhsNumber: '' };

export type RedRobinInformationGatheringDestination = 'red-robin' | 'messaging' | 'care-navigation';

export function useInformationGatheringNavigation() {
	const controller = useResolveController(ViewRedRobinController);
	const informationGathering = useInformationGathering();
	const patientNinStorage = useStorage('careNavigation_patientNin');

	const continueToDestination = useCallback(
		(
			role: InformationGatheringRole,
			destination: RedRobinInformationGatheringDestination,
			patientIdentity: InformationGatheringPatientIdentity,
			state?: CompletedInformationGatheringState,
		) => {
			const submissionId = state?.informationGathering.key ?? null;
			switch (destination) {
				case 'red-robin':
					void controller.createSession(
						{
							AsUnverifiedRepresentativeForNin: submissionId || role === 'self' ? null : patientIdentity.nhsNumber || null,
							DateOfBirth: submissionId ? null : patientIdentity.dateOfBirth || null,
							InformationGatheringSubmissionId: submissionId,
						},
						state,
					);
					break;
				case 'messaging':
					controller.goToMessaging(submissionId ? null : patientIdentity.nhsNumber || null, state);
					break;
				case 'care-navigation':
					patientNinStorage.set(patientIdentity.nhsNumber);
					controller.goToCareNavigationDescriptionOfMatter({ replace: true, state });
					break;
			}
		},
		[controller, patientNinStorage],
	);

	const start = useCallback(
		async (
			role: InformationGatheringRole,
			destination: RedRobinInformationGatheringDestination,
			patientIdentity: InformationGatheringPatientIdentity = EMPTY_PATIENT_IDENTITY,
		) => {
			const result = await informationGathering.begin({ role, patientIdentity, preselectedSexAssignedAtBirth: null, destination });
			switch (result.kind) {
				case 'bypass':
					continueToDestination(role, destination, patientIdentity);
					break;
				case 'collect':
					controller.goToScreen(RedRobinScreen.InformationGathering, {
						state: { ...result.state, redRobinInformationGatheringDestination: destination },
					});
					break;
				case 'blocked':
					controller.goToScreen(result.reason === 'nhs-login-required' ? RedRobinScreen.NhsLoginRequired : RedRobinScreen.Error, {
						replace: true,
					});
					break;
			}
		},
		[controller, continueToDestination, informationGathering],
	);

	const complete = useCallback(
		async (
			destination: RedRobinInformationGatheringDestination,
			entry: InformationGatheringEntry,
			draft: InformationGatheringDraft,
			patientIdentity: InformationGatheringPatientIdentity,
		): Promise<boolean> => {
			const submissionId = await informationGathering.submit(entry, draft, patientIdentity);
			if (submissionId === null) return false;

			continueToDestination(entry.role, destination, patientIdentity, createCompletedInformationGatheringState({ submissionId }));
			return true;
		},
		[continueToDestination, informationGathering],
	);

	return useMemo(() => ({ start, complete }), [complete, start]);
}

export function getRedRobinInformationGatheringDestination(state: unknown): RedRobinInformationGatheringDestination | null {
	if (typeof state !== 'object' || state === null || !('redRobinInformationGatheringDestination' in state)) return null;
	const destination = state.redRobinInformationGatheringDestination;
	return destination === 'red-robin' || destination === 'messaging' || destination === 'care-navigation' ? destination : null;
}
